import {
  FormField,
  NonCancelableCustomEvent,
  Select,
  SelectProps,
  TextContent,
  TokenGroup,
} from '@amzn/awsui-components-core';
import { Component } from 'react';
import React from 'react';
import constants from '../../../common/constants';
import InfoLink from '../../common/InfoLink';
import { getTopServicesText } from '../../utils/MetadataUtils';

interface Props {
  onSelectUpdated?: (sectionId: string, selectedId: string, validationError?: string) => void;
  onValidationError?: (validationError?: string) => void;
  sectionId?: string;
  label: string;
  description?: string;
  contextualHelp?: string;
  readonly?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  showValidationError?: boolean;
  selectedId: string;
  selectOptions: SelectProps.Option[];
  stretch?: boolean;
  placeholder?: string;
  visible?: boolean;
  detailsMap?: any;
}

interface State {
  selectedId: string;
  validationError?: string;
}

class MetadataSelectInput extends Component<Props, State> {
  static defaultProps = {
    disabled: false,
    isRequired: false,
    placeholder: '',
    readonly: false,
    showValidationError: true,
    stretch: false,
    visible: true,
  };

  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    const selectedId = props.selectedId;
    const validationError = this.validateInput(selectedId);
    this.state = {
      selectedId,
      validationError,
    };
    this.updateSectionValue(selectedId, validationError);
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      this.props.selectedId !== prevProps.selectedId &&
      this.props.selectedId !== this.state.selectedId
    ) {
      this.setState({
        selectedId: this.props.selectedId,
      });
      this.validateInput(this.props.selectedId);
    }

    if (prevProps.showValidationError && !this.props.showValidationError) {
      this.setState({
        validationError: undefined,
      });
    }
  }

  public render() {
    const {
      readonly,
      label,
      description,
      contextualHelp,
      stretch,
      disabled,
      selectOptions,
      placeholder,
      isRequired,
      visible,
      showValidationError,
      detailsMap,
    } = this.props;
    const { selectedId, validationError } = this.state;
    if (!visible) {
      return null;
    } else if (readonly) {
      return selectedId ? (
        <details open>
          <summary>{label}</summary>
          <TextContent>
            <ul>
              <li>{this.getItemLabel(selectedId)}</li>
            </ul>
          </TextContent>
        </details>
      ) : null;
    } else {
      const details = detailsMap?.get(selectedId);
      const detailsToken =
        !!details && !!selectedId ? (
          <TokenGroup
            items={[
              {
                label: this.getItemLabel(selectedId),
                description: details.topics,
                tags: details.services ? ['Services: ' + getTopServicesText(details.services)] : [],
              },
            ]}
            onDismiss={this.handleDismiss}
          />
        ) : null;
      return (
        <FormField
          errorText={showValidationError && validationError}
          label={
            <span>
              {label}
              {!isRequired && <i> - optional</i>}
            </span>
          }
          info={contextualHelp && <InfoLink topic={contextualHelp} />}
          description={description}
          stretch={stretch}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Select
            selectedOption={selectOptions.find((option) => option.value === selectedId) ?? null}
            onChange={this.handleInput}
            options={selectOptions}
            placeholder={placeholder}
            disabled={disabled}
            ariaRequired={isRequired}
          />
          {detailsToken}
        </FormField>
      );
    }
  }

  private handleDismiss = () => {
    const error = this.validateInput('');
    this.setState({
      selectedId: '',
      validationError: error,
    });
    this.updateSectionValue('', error);
  };

  private handleInput = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    // @ts-expect-error TS2322 - strict migration
    const value: string = event.detail.selectedOption?.value;
    const error = this.validateInput(value);

    this.setState({
      selectedId: value,
      validationError: error,
    });

    this.updateSectionValue(value, error);
  };

  private validateInput = (value: string) => {
    const { isRequired, onValidationError } = this.props;

    let error: string;

    if (isRequired && !value) {
      error = constants.REQUIRED;
    }

    if (onValidationError) {
      // @ts-expect-error TS2454 - strict migration
      onValidationError(error);
    }

    // @ts-expect-error TS2454 - strict migration
    return error;
  };

  private getItemLabel = (id: string) => {
    const { selectOptions } = this.props;
    const item = selectOptions.find((o) => o.value === id);
    return item ? item.label : '';
  };

  private updateSectionValue = (value: string, error?: string) => {
    const { sectionId, onSelectUpdated: onUpdated } = this.props;
    if (sectionId && onUpdated) {
      onUpdated(sectionId, value, error);
    }
  };
}

export default MetadataSelectInput;
